import React from "react";
import { motion } from "framer-motion";
import { Award, ArrowRight } from "lucide-react";
import { Program } from "../../types/program";
import { replaceHarvardWithIvyLeague } from "../../utils/utils";

interface HeroProps {
  program: Program;
}

const Hero: React.FC<HeroProps> = ({ program }) => {
  return (
    <section className="bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900 text-white relative overflow-hidden">
      <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.05%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-40"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-indigo-500/20 to-blue-500/20 backdrop-blur-sm rounded-full text-indigo-300 text-sm font-semibold mb-6 border border-indigo-400/30">
            <Award className="w-4 h-4 mr-2" />
            CAMBRIDGE CERTIFIED • WORLD-CLASS EDUCATION
          </div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight">
            {(() => {
              const title = replaceHarvardWithIvyLeague(
                program.title
              ).toUpperCase();
              const words = title.split(" ");
              const middleIndex = Math.floor(words.length / 2);

              return words.map((word, index) => {
                if (index === middleIndex && words.length > 1) {
                  return (
                    <span
                      key={index}
                      className="block bg-gradient-to-r from-blue-400 via-cyan-300 to-purple-300 bg-clip-text text-transparent"
                    >
                      {word}
                    </span>
                  );
                }
                return (
                  <span key={index} className="block">
                    {word}
                  </span>
                );
              });
            })()}
          </h1>

          <p className="text-lg md:text-xl text-blue-200 max-w-4xl mx-auto mb-8 leading-relaxed">
            {replaceHarvardWithIvyLeague(program.description)}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <button
              onClick={() => {
                const element = document.getElementById("program-selection");
                if (element) {
                  const headerOffset = 120;
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition =
                    elementPosition + window.pageYOffset - headerOffset;
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: "smooth",
                  });
                }
              }}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-2xl px-8 py-4 rounded-lg font-semibold flex items-center transition-all duration-300 transform hover:scale-105"
            >
              <span className="flex items-center">
                View Programs
                <ArrowRight className="ml-2 h-5 w-5" />
              </span>
            </button>
            <button
              onClick={() => {
                const element = document.getElementById("program-details");
                if (element) {
                  const headerOffset = 120;
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition =
                    elementPosition + window.pageYOffset - headerOffset;
                  window.scrollTo({
                    top: offsetPosition,
                    behavior: "smooth",
                  });
                }
              }}
              className="border-2 border-blue-300 text-blue-300 hover:bg-blue-300 hover:text-blue-900 px-8 py-4 rounded-lg font-semibold transition-all duration-300"
            >
              Learn More
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
